import z from 'zod'

export const ASPECT_RATIO_PATTERN = /^\d+:\d+$/

/** Canvas aspect ratios are persisted as a free-form `W:H` string, because the
 *  custom-ratio input accepts arbitrary integer pairs. Storage stays open; the
 *  design pipeline renders the named presets below and snaps anything else. */
export const zAspectRatio = z.custom<string>(
  (v) => typeof v === 'string' && ASPECT_RATIO_PATTERN.test(v),
)

/** Parse `W:H` into its two components. Returns null for malformed input or a
 *  zero on either side, so callers can pick their own fallback rather than
 *  propagating a NaN through layout maths. */
export const parseAspectRatio = (aspectRatio: string): { width: number; height: number } | null => {
  const [width, height] = aspectRatio.split(':').map(Number)
  if (!width || !height || !Number.isFinite(width) || !Number.isFinite(height)) return null
  return { width, height }
}

/** Pixel dimensions for a canvas of the given ratio, short edge pinned to 1024.
 *  Node coordinates are absolute against this box, so the browser editor and
 *  the headless Playwright render must size identically. Keep this the only
 *  implementation. */
export const calculateCanvasSize = (aspectRatio: string): { width: number; height: number } => {
  const minSize = 1024
  const parsed = parseAspectRatio(aspectRatio)
  if (!parsed) return { width: minSize, height: minSize }

  const ratio = parsed.width / parsed.height
  return ratio > 1
    ? { width: Math.round(minSize * ratio), height: minSize }
    : { width: minSize, height: Math.round(minSize / ratio) }
}

/** The named ratios the editor offers and the design pipeline renders. Listed
 *  landscape to portrait, which is the order the editor picker draws them in.
 *
 *  The layout LLM is not prompted with the ratio itself, only with the pixel
 *  canvas from `calculateCanvasSize`, so this list is a product choice rather
 *  than a model constraint. The ceiling that is real: the Playwright renderer
 *  caps a side at 4096px (`services/playwright/src/routes/renderLayout.ts`),
 *  which with a 1024 short edge means nothing beyond 4:1 can render. */
export const PRESET_ASPECT_RATIOS = [
  '16:9',
  '3:2',
  '4:3',
  '5:4',
  '1:1',
  '4:5',
  '3:4',
  '2:3',
  '9:16',
] as const
export type PresetAspectRatio = (typeof PRESET_ASPECT_RATIOS)[number]
export const zPresetAspectRatio = z.enum(PRESET_ASPECT_RATIOS)

export const isPresetAspectRatio = (value: unknown): value is PresetAspectRatio =>
  typeof value === 'string' && (PRESET_ASPECT_RATIOS as readonly string[]).includes(value)

const TIE_EPSILON = 1e-9

/** 1 landscape, -1 portrait, 0 square. */
const orientationOf = (ratio: number): number => (ratio > 1 ? 1 : ratio < 1 ? -1 : 0)

/** Nearest preset to a raw width/height ratio. Distance is measured in log
 *  space so portrait and landscape candidates are weighted symmetrically. */
const nearestPreset = (ratio: number): PresetAspectRatio => {
  const target = Math.log(ratio)
  const sourceOrientation = orientationOf(ratio)

  let best: PresetAspectRatio = '1:1'
  let bestDiff = Infinity
  let bestKeepsOrientation = false

  for (const candidate of PRESET_ASPECT_RATIOS) {
    const [width, height] = candidate.split(':').map(Number) as [number, number]
    const candidateRatio = width / height
    const diff = Math.abs(target - Math.log(candidateRatio))
    const keepsOrientation = orientationOf(candidateRatio) === sourceOrientation

    // Exact ties happen: 4:3 sits precisely between 1:1 and 16:9 in log space,
    // because 16:9 is 4:3 squared. Break toward the candidate that keeps the
    // design landscape or portrait rather than whichever is listed first.
    const isCloser = diff < bestDiff - TIE_EPSILON
    const isBetterTie =
      Math.abs(diff - bestDiff) <= TIE_EPSILON && keepsOrientation && !bestKeepsOrientation

    if (isCloser || isBetterTie) {
      best = candidate
      bestDiff = diff
      bestKeepsOrientation = keepsOrientation
    }
  }

  return best
}

/** Map a stored ratio onto the nearest preset.
 *
 *  Every design-generation entry point crosses this boundary, because a saved
 *  canvas can carry a custom `W:H` the pipeline has no preset for. Passing one
 *  through unchecked is what broke "modify design". Callers with a UI should
 *  compare the result against their input and tell the user when the shape
 *  changed. */
export const toPresetAspectRatio = (aspectRatio: string | undefined | null): PresetAspectRatio => {
  if (!aspectRatio) return '1:1'
  if (isPresetAspectRatio(aspectRatio)) return aspectRatio

  const parsed = parseAspectRatio(aspectRatio)
  if (!parsed) return '1:1'

  return nearestPreset(parsed.width / parsed.height)
}

/** Nearest preset to a set of pixel dimensions. Used when a source image or
 *  video frame becomes a canvas and has to adopt a ratio. */
export const presetAspectRatioForSize = (width: number, height: number): PresetAspectRatio => {
  if (!width || !height) return '1:1'
  return nearestPreset(width / height)
}
